import { useTranslations } from '@vocab/react';
import {
  TabsProvider,
  Stack,
  Tabs,
  Tab,
  TabPanels,
  TabPanel,
  Columns,
  Column,
  Badge,
} from 'braid-design-system';
import type React from 'react';

import { useTracking } from 'src/App/context/TrackingProvider';
import translations from 'src/App/pages/ad-budget-selection/.vocab/index';
import { mapLegacyAdBudgetType } from 'src/App/utils/analytics/event';
import { getLogger } from 'src/App/utils/logger/logger';

import {
  mapBudgetOptionsToBudgetPrices,
  type AdBudgetSelectionPageBaseEvent,
} from '../../eventTracking/createEvent';
import type { BudgetOption } from '../../mapBudgetOption';
import { BudgetPanel } from '../BudgetPanel/BudgetPanel';

interface BudgetOptionsProps {
  budgetOptions: BudgetOption[];
  selectedBudgetType: string;
  setSelectedBudgetType: React.Dispatch<React.SetStateAction<string>>;
  trackingBaseEvent: AdBudgetSelectionPageBaseEvent;
  productSetId: string;
  showBuyNowButton: boolean;
}

const tabOrder = ['FlexibleBudget', 'BrandedBudget', 'PremiumBudget'];

const tabNames = {
  FlexibleBudget: 'Flexible Ad Budget',
  BrandedBudget: 'Branded Ad Budget',
  PremiumBudget: 'Premium Ad Budget',
};

export const BudgetOptions = ({
  budgetOptions,
  selectedBudgetType,
  setSelectedBudgetType,
  trackingBaseEvent,
  productSetId,
  showBuyNowButton,
}: BudgetOptionsProps) => {
  const availableBudgetTypes = Array.from(
    new Set(budgetOptions.map((budget) => budget.budgetType)),
  ).sort((a, b) => tabOrder.indexOf(a) - tabOrder.indexOf(b));
  const shouldShowTabs = availableBudgetTypes.length > 1;
  const mostNumberOfDiscounts = budgetOptions
    .filter((budgetOption) => budgetOption.budgetType === selectedBudgetType)
    .reduce((most, budgetOption) => {
      const numberOfDiscounts = budgetOption.discounts?.length ?? 0;
      return numberOfDiscounts > most ? numberOfDiscounts : most;
    }, 0);

  const { trackLink } = useTracking();
  const { t } = useTranslations(translations);

  return shouldShowTabs ? (
    <TabsProvider
      id="adBudgetSelector"
      selectedItem={selectedBudgetType}
      onChange={(_index, selectedBudgetTab) => {
        if (selectedBudgetTab) {
          let eventName;
          switch (selectedBudgetTab) {
            case 'BrandedBudget':
              eventName = 'seek_contract_branded_budget_tab_pressed';
              break;
            case 'FlexibleBudget':
              eventName = 'seek_contract_flexible_budget_tab_pressed';
              break;
            case 'PremiumBudget':
              eventName = 'seek_contract_premium_budget_tab_pressed';
              break;
            default:
              eventName = 'seek_contract_unknown_budget_tab_pressed';
              getLogger().error(`Unexpected budgetType: ${selectedBudgetTab}`);
          }

          trackLink({
            eventName,
            data: {
              ...trackingBaseEvent,
              adBudgetType: mapLegacyAdBudgetType(selectedBudgetTab),
              adBudgetPrices: mapBudgetOptionsToBudgetPrices(
                budgetOptions.filter(
                  ({ budgetType }) => budgetType === selectedBudgetTab,
                ),
              ),
            },
          });

          setSelectedBudgetType(selectedBudgetTab);
        }
      }}
    >
      <Stack space="medium">
        <Tabs label="AdBudgetsPurchase" align="center">
          {availableBudgetTypes.map((budgetType) => (
            <Tab
              key={budgetType}
              item={budgetType}
              badge={
                budgetType === 'PremiumBudget' ? (
                  <Badge tone="positive">{t('new')}</Badge>
                ) : undefined
              }
            >
              {tabNames[budgetType]}
            </Tab>
          ))}
        </Tabs>
        <TabPanels>
          {availableBudgetTypes.map((budgetType) => (
            <TabPanel key={budgetType} item={budgetType}>
              <Columns space="medium" collapseBelow="desktop">
                {budgetOptions
                  .filter(
                    (budgetOption) => budgetOption.budgetType === budgetType,
                  )
                  .map((budgetOption) => (
                    <Column key={budgetOption.budgetName}>
                      <BudgetPanel
                        budgetOption={budgetOption}
                        trackingBaseEvent={trackingBaseEvent}
                        mostNumberOfDiscounts={mostNumberOfDiscounts}
                        productSetId={productSetId}
                        showBuyNowButton={showBuyNowButton}
                      />
                    </Column>
                  ))}
              </Columns>
            </TabPanel>
          ))}
        </TabPanels>
      </Stack>
    </TabsProvider>
  ) : (
    <Columns space="medium" collapseBelow="desktop">
      {budgetOptions.map((budgetOption) => (
        <Column key={budgetOption.budgetName} width="1/3">
          <BudgetPanel
            budgetOption={budgetOption}
            trackingBaseEvent={trackingBaseEvent}
            mostNumberOfDiscounts={mostNumberOfDiscounts}
            productSetId={productSetId}
            showBuyNowButton={showBuyNowButton}
          />
        </Column>
      ))}
    </Columns>
  );
};
